/**
 * FitTip card — appears in main feed every 3-4 posts.
 * Spec: gold left border, "EXPERT TIP" badge, save + book session.
 */
import { useState } from "react";
import { Bookmark, Shield, MoreHorizontal, Flag } from "lucide-react";
import { Link } from "react-router-dom";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import ReportTipSheet from "./ReportTipSheet";

export default function FitTipCard({ tip, onChange }) {
  const [saved, setSaved] = useState(!!tip.saved_by_me);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [reportSheet, setReportSheet] = useState(false);

  const submitReport = async (reason, detail_text) => {
    try {
      await apiClient.post(`/community/report`, {
        content_type: "tip",
        content_id: tip.tip_id,
        reason,
        detail_text: detail_text || null,
      });
      toast.success("Thanks. Our team will review this.");
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === "already_reported") toast.info("You already reported this.");
      else if (err === "cannot_report_own") toast.error("You can't report your own tip.");
      else if (err === "rate_limited") toast.error("Daily report limit reached (20).");
      else toast.error("Could not report");
    }
  };

  const toggleSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (saved) {
        await apiClient.delete(`/fittips/${tip.tip_id}/save`);
        setSaved(false);
      } else {
        await apiClient.post(`/fittips/${tip.tip_id}/save`);
        setSaved(true);
        toast.success("Saved to Tips Library");
      }
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`fittip-${tip.tip_id}`}
      style={{
        background: "var(--bg-surface, #E4E2DE)",
        borderLeft: "3px solid #F5C518",
        borderRadius: 16,
        overflow: "hidden",
        padding: "14px 16px 16px",
        position: "relative",
      }}
    >
      <button
        onClick={() => setMenu((v) => !v)}
        data-testid={`fittip-menu-${tip.tip_id}`}
        aria-label="More"
        style={{
          position: "absolute", top: 10, right: 10,
          width: 28, height: 28, borderRadius: 999,
          background: "transparent", border: "none",
          display: "grid", placeItems: "center",
          color: "var(--text-tertiary, #8A8884)",
        }}
      >
        <MoreHorizontal size={18} />
      </button>
      {menu && (
        <div
          data-testid={`fittip-menu-panel-${tip.tip_id}`}
          style={{
            position: "absolute", top: 34, right: 10, zIndex: 10,
            background: "#fff", border: "1px solid var(--border, rgba(12,12,11,0.16))",
            borderRadius: 10, padding: 4, minWidth: 140,
          }}
        >
          <button
            onClick={() => { setMenu(false); setReportSheet(true); }}
            data-testid={`fittip-report-${tip.tip_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px]"
            style={{ background: "transparent", border: "none", color: "#DC2626" }}
          >
            <Flag size={14} /> Report
          </button>
        </div>
      )}
      {reportSheet && (
        <ReportTipSheet
          onCancel={() => setReportSheet(false)}
          onSubmit={(reason, detail) => { setReportSheet(false); submitReport(reason, detail); }}
        />
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span
          style={{
            display: "inline-block",
            background: "#F5C518",
            color: "#0C0C0B",
            fontFamily: "Barlow, sans-serif",
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            padding: "3px 8px",
            borderRadius: 100,
          }}
        >
          Expert Tip
        </span>
        {tip.arena_gym_match && (
          <span
            data-testid="fittip-arena-badge"
            style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              background: "rgba(245,197,24,0.12)",
              color: "#A37B00",
              border: "1px solid rgba(245,197,24,0.45)",
              fontSize: 9, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.06em",
              padding: "2px 7px", borderRadius: 100,
            }}
          >
            <Shield size={9} /> From your gym
          </span>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
        {tip.is_official ? (
          <div
            style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "#0C0C0B", display: "grid", placeItems: "center",
            }}
          >
            <Shield size={16} style={{ color: "#F5C518" }} />
          </div>
        ) : tip.trainer_avatar ? (
          <img src={tip.trainer_avatar} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
        ) : (
          <div
            style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "rgba(245,197,24,0.18)", color: "#0C0C0B",
              display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700,
            }}
          >
            {(tip.trainer_name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div>
          <div style={{ fontFamily: "Barlow", fontSize: 13, fontWeight: 600, color: "var(--text-primary, #0C0C0B)" }}>
            {tip.is_official ? "FitFrik" : tip.trainer_name}
          </div>
          <div style={{ fontFamily: "Barlow", fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
            {tip.is_official ? "Official" : (tip.trainer_role || "Verified Pro")} · {tip.category}
          </div>
        </div>
      </div>

      <p
        style={{
          fontFamily: "Barlow",
          fontSize: 15,
          fontWeight: 500,
          lineHeight: 1.6,
          color: "var(--text-primary, #0C0C0B)",
          marginTop: 10,
          whiteSpace: "pre-wrap",
        }}
      >
        {tip.tip_text}
      </p>
      {tip.image_url && (
        <img
          src={tip.image_url}
          alt=""
          style={{ display: "block", width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 10, marginTop: 10 }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
        <button
          onClick={toggleSave}
          data-testid={`fittip-save-${tip.tip_id}`}
          disabled={busy}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "transparent", border: "none",
            fontFamily: "Barlow", fontSize: 13, fontWeight: 600,
            color: saved ? "#0C0C0B" : "var(--text-tertiary, #8A8884)",
          }}
        >
          <Bookmark size={15} fill={saved ? "#F5C518" : "none"} stroke={saved ? "#F5C518" : "currentColor"} />
          {saved ? "Saved" : "Save"}
          {tip.saves_count ? ` · ${tip.saves_count}` : ""}
        </button>
        {!tip.is_official && tip.trainer_id && (
          <Link
            to={`/trainers/${tip.trainer_id}`}
            data-testid={`fittip-book-${tip.tip_id}`}
            style={{
              fontFamily: "Barlow", fontSize: 13, fontWeight: 700,
              color: "#00B0D5", textDecoration: "none",
            }}
          >
            Book session →
          </Link>
        )}
      </div>
    </article>
  );
}
